import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchAgentByWorkflow, fetchFieldsValues } from "../../service/Task";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import CommonButton from "../common/CommonButton";
import translations from "../common/Translations";
import ReactSelector from "../common/CommonReactSelector";
import { components } from 'react-select';
import { FaSpinner } from "react-icons/fa6";
import { Search, X } from "lucide-react";
import { CreateMultipleRcWithOrg, CreateRc } from "../../service/CustomerSupport";
import MultiAttachInput from "../common/MultiAttachInput";
import { validateAttachments } from "../../utils/attachments";

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};

// Same canonical field height/style as Interactive_Rc.jsx/CreateInstallment.jsx
// so every Customer Support tab lines up at the same 50px.
const FIELD_INPUT_CLASS =
  "h-[50px] px-4 rounded-lg border border-gray-300 bg-white text-gray-700 w-full text-sm focus:border-blue-500 focus:outline-none focus:ring-0";

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const SpecialRC = ({ activityId, customVarPerm, onHeaderActionsChange }) => {
  const lastItemRef = useRef(null);
  const [fieldAgents, setFieldAgents] = useState([]);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(""); // State to store the selected assigneeId
  const [isInteractiveRC, setIsInteractiveRC] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [accountNo, setAccountNo] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [remarks, setRemarks] = useState("");
  const [remarksError, setRemarksError] = useState(""); // Error state for remarks
  const [fromDate, setFromDate] = useState(""); // State for "From Date"
  const [toDate, setToDate] = useState(""); // State for "To Date"
  const [proofFiles, setProofFiles] = useState([]); // optional attachments, up to MAX_ATTACHMENTS
  const { userType, theme, langMode, projectId } = useContext(AppContext);
  const [orgId, setOrgId] = useState("2");
  const [countryDetail, setCountryDetail] = useState([
    { name: "Afghanistan", length: 9, countryCode: "+93" },
    { name: "Albania", length: 8, countryCode: "+355" },
    { name: "Algeria", length: 9, countryCode: "+213" },
    { name: "Andorra", length: 6, countryCode: "+376" },
    { name: "Angola", length: 9, countryCode: "+244" },
    { name: "Argentina", length: 10, countryCode: "+54" },
    { name: "Armenia", length: 8, countryCode: "+374" },
    { name: "Aruba", length: 7, countryCode: "+297" },
    { name: "Australia", length: 9, countryCode: "+61" },
    { name: "Austria", length: 12, countryCode: "+43" },
    { name: "Azerbaijan", length: 9, countryCode: "+994" },
    { name: "Antigua and Barbuda", length: 7, countryCode: "+1268" },
    { name: "Bahamas", length: 7, countryCode: "+1 242" },
    { name: "Bahrain", length: 8, countryCode: "+973" },
    { name: "Bangladesh", length: 10, countryCode: "+880" },
    { name: "Barbados", length: 10, countryCode: "+1 246" },
    { name: "Belarus", length: 9, countryCode: "+375" },
    { name: "Belgium", length: 9, countryCode: "+32" },
    { name: "Belize", length: 7, countryCode: "+501" },
    { name: "Benin", length: 8, countryCode: "+229" },
    { name: "Bhutan", length: 8, countryCode: "+975" },
    { name: "Bolivia", length: 8, countryCode: "+591" },
    { name: "Bosnia and Herzegovina", length: 8, countryCode: "+387" },
    { name: "Botswana", length: 7, countryCode: "+267" },
    { name: "Brazil", length: 10, countryCode: "+55" },
    { name: "Brunei", length: 7, countryCode: "+673" },
    { name: "Bulgaria", length: 8, countryCode: "+359" },
    { name: "Burkina Faso", length: 10, countryCode: "+226" },
    { name: "Burundi", length: 8, countryCode: "+257" },
    { name: "British Indian Ocean Territiry", length: 7, countryCode: "+246" },
    { name: "Cabo Verde", length: 7, countryCode: "+238" },
    { name: "Cambodia", length: 9, countryCode: "+855" },
    { name: "Cameroon", length: 9, countryCode: "+237" },
    { name: "Canada", length: 11, countryCode: "+1" },
    { name: "Central African Republic", length: 10, countryCode: "+236" },
    { name: "Chad", length: 8, countryCode: "+235" },
    { name: "Chile", length: 9, countryCode: "+56" },
    { name: "China", length: 11, countryCode: "+86" },
    { name: "Colombia", length: 10, countryCode: "+57" },
    { name: "Comoros", length: 7, countryCode: "+269" },
    { name: "Congo (Congo-Brazzaville)", length: 10, countryCode: "+242" },
    { name: "Costa Rica", length: 8, countryCode: "+506" },
    { name: "Croatia", length: 9, countryCode: "+385" },
    { name: "Cuba", length: 8, countryCode: "+53" },
    { name: "Cyprus", length: 8, countryCode: "+357" },
    { name: "Czech Republic", length: 9, countryCode: "+420" },
    { name: "Denmark", length: 8, countryCode: "+45" },
    { name: "Djibouti", length: 7, countryCode: "+253" },
    { name: "Dominica", length: 10, countryCode: "+1 767" },
    { name: "Dominican Republic", length: 10, countryCode: "+1 809" },
    { name: "Ecuador", length: 9, countryCode: "+593" },
    { name: "Egypt", length: 10, countryCode: "+20" },
    { name: "El Salvador", length: 8, countryCode: "+503" },
    { name: "Equatorial Guinea", length: 9, countryCode: "+240" },
    { name: "Eritrea", length: 8, countryCode: "+291" },
    { name: "Estonia", length: 7, countryCode: "+372" },
    { name: "Eswatini", length: 8, countryCode: "+268" },
    { name: "Ethiopia", length: 9, countryCode: "+251" },
    { name: "Fiji", length: 7, countryCode: "+679" },
    { name: "Finland", length: 12, countryCode: "+358" },
    { name: "France", length: 9, countryCode: "+33" },
    { name: "Gabon", length: 8, countryCode: "+241" },
    { name: "Gambia", length: 7, countryCode: "+220" },
    { name: "Georgia", length: 9, countryCode: "+995" },
    { name: "Germany", length: 11, countryCode: "+49" },
    { name: "Ghana", length: 9, countryCode: "+233" },
    { name: "Greece", length: 10, countryCode: "+30" },
    { name: "Grenada", length: 10, countryCode: "+1 473" },
    { name: "Guatemala", length: 8, countryCode: "+502" },
    { name: "Guinea", length: 9, countryCode: "+224" },
    { name: "Guinea-Bissau", length: 7, countryCode: "+245" },
    { name: "Guyana", length: 10, countryCode: "+592" },
    { name: "Haiti", length: 8, countryCode: "+509" },
    { name: "Honduras", length: 8, countryCode: "+504" },
    { name: "Hungary", length: 9, countryCode: "+36" },
    { name: "Iceland", length: 7, countryCode: "+354" },
    { name: "India", length: 10, countryCode: "+91" },
    { name: "Indonesia", length: 10, countryCode: "+62" },
    { name: "Iran", length: 10, countryCode: "+98" },
    { name: "Iraq", length: 10, countryCode: "+964" },
    { name: "Ireland", length: 7, countryCode: "+353" },
    { name: "Israel", length: 9, countryCode: "+972" },
    { name: "Italy", length: 10, countryCode: "+39" },
    { name: "Jamaica", length: 10, countryCode: "+1 876" },
    { name: "Japan", length: 10, countryCode: "+81" },
    { name: "Jordan", length: 9, countryCode: "+962" },
    { name: "Kazakhstan", length: 10, countryCode: "+7" },
    { name: "Kenya", length: 10, countryCode: "+254" },
    { name: "Kiribati", length: 7, countryCode: "+686" },
    { name: "Kuwait", length: 8, countryCode: "+965" },
    { name: "Kyrgyzstan", length: 9, countryCode: "+996" },
    { name: "Laos", length: 8, countryCode: "+856" },
    { name: "Latvia", length: 8, countryCode: "+371" },
    { name: "Lebanon", length: 8, countryCode: "+961" },
    { name: "Lesotho", length: 8, countryCode: "+266" },
    { name: "Liberia", length: 7, countryCode: "+231" },
    { name: "Libya", length: 9, countryCode: "+218" },
    { name: "Liechtenstein", length: 10, countryCode: "+423" },
    { name: "Lithuania", length: 9, countryCode: "+370" },
    { name: "Luxembourg", length: 9, countryCode: "+352" },
    { name: "Madagascar", length: 9, countryCode: "+261" },
    { name: "Malawi", length: 10, countryCode: "+265" },
    { name: "Malaysia", length: 10, countryCode: "+60" },
    { name: "Maldives", length: 7, countryCode: "+960" },
    { name: "Mali", length: 8, countryCode: "+223" },
    { name: "Malta", length: 8, countryCode: "+356" },
    { name: "Marshall Islands", length: 7, countryCode: "+692" },
    { name: "Mauritania", length: 8, countryCode: "+222" },
    { name: "Mauritius", length: 10, countryCode: "+230" },
    { name: "Mexico", length: 10, countryCode: "+52" },
    { name: "Micronesia", length: 7, countryCode: "+691" },
    { name: "Moldova", length: 8, countryCode: "+373" },
    { name: "Monaco", length: 8, countryCode: "+377" },
    { name: "Mongolia", length: 8, countryCode: "+976" },
    { name: "Montenegro", length: 8, countryCode: "+382" },
    { name: "Morocco", length: 10, countryCode: "+212" },
    { name: "Mozambique", length: 9, countryCode: "+258" },
    { name: "Myanmar (formerly Burma)", length: 9, countryCode: "+95" },
    { name: "Namibia", length: 9, countryCode: "+264" },
    { name: "Nauru", length: 7, countryCode: "+674" },
    { name: "Nepal", length: 10, countryCode: "+977" },
    { name: "Netherlands", length: 10, countryCode: "+31" },
    { name: "New Zealand", length: 9, countryCode: "+64" },
    { name: "Nicaragua", length: 8, countryCode: "+505" },
    { name: "Niger", length: 8, countryCode: "+227" },
    { name: "Nigeria", length: 10, countryCode: "+234" },
    { name: "North Korea", length: 9, countryCode: "+850" },
    { name: "North Macedonia", length: 8, countryCode: "+389" },
    { name: "Norway", length: 8, countryCode: "+47" },
    { name: "Oman", length: 8, countryCode: "+968" },
    { name: "Pakistan", length: 10, countryCode: "+92" },
    { name: "Palau", length: 7, countryCode: "+680" },
    { name: "Palestine", length: 10, countryCode: "+970" },
    { name: "Panama", length: 7, countryCode: "+507" },
    { name: "Papua New Guinea", length: 10, countryCode: "+675" },
    { name: "Paraguay", length: 9, countryCode: "+595" },
    { name: "Peru", length: 9, countryCode: "+51" },
    { name: "Philippines", length: 10, countryCode: "+63" },
    { name: "Poland", length: 9, countryCode: "+48" },
    { name: "Portugal", length: 9, countryCode: "+351" },
    { name: "Qatar", length: 8, countryCode: "+974" },
    { name: "Romania", length: 10, countryCode: "+40" },
    { name: "Russia", length: 10, countryCode: "+7" },
    { name: "Rwanda", length: 10, countryCode: "+250" },
    { name: "Saint Kitts and Nevis", length: 10, countryCode: "+1 869" },
    { name: "Saint Lucia", length: 10, countryCode: "+1 758" },
    {
      name: "Saint Vincent and the Grenadines",
      length: 10,
      countryCode: "+1 784",
    },
    { name: "Samoa", length: 7, countryCode: "+685" },
    { name: "San Marino", length: 10, countryCode: "+378" },
    { name: "Sao Tome and Principe", length: 7, countryCode: "+239" },
    { name: "Saudi Arabia", length: 9, countryCode: "+966" },
    { name: "Senegal", length: 9, countryCode: "+221" },
    { name: "Serbia", length: 8, countryCode: "+381" },
    { name: "Seychelles", length: 7, countryCode: "+248" },
    { name: "Sierra Leone", length: 8, countryCode: "+232" },
    { name: "Singapore", length: 8, countryCode: "+65" },
    { name: "Slovakia", length: 9, countryCode: "+421" },
    { name: "Slovenia", length: 8, countryCode: "+386" },
    { name: "Solomon Islands", length: 7, countryCode: "+677" },
    { name: "Somalia", length: 8, countryCode: "+252" },
    { name: "South Africa", length: 10, countryCode: "+27" },
    { name: "South Korea", length: 10, countryCode: "+82" },
    { name: "South Sudan", length: 9, countryCode: "+211" },
    { name: "Spain", length: 9, countryCode: "+34" },
    { name: "Sri Lanka", length: 10, countryCode: "+94" },
    { name: "Sudan", length: 9, countryCode: "+249" },
    { name: "Suriname", length: 7, countryCode: "+597" },
    { name: "Sweden", length: 10, countryCode: "+46" },
    { name: "Switzerland", length: 9, countryCode: "+41" },
    { name: "Syria", length: 9, countryCode: "+963" },
    { name: "Taiwan", length: 9, countryCode: "+886" },
    { name: "Tajikistan", length: 9, countryCode: "+992" },
    { name: "Tanzania", length: 10, countryCode: "+255" },
    { name: "Thailand", length: 9, countryCode: "+66" },
    { name: "Timor-Leste", length: 9, countryCode: "+670" },
    { name: "Togo", length: 8, countryCode: "+228" },
    { name: "Tonga", length: 7, countryCode: "+676" },
    { name: "Trinidad and Tobago", length: 10, countryCode: "+1 868" },
    { name: "Tunisia", length: 8, countryCode: "+216" },
    { name: "Turkey", length: 10, countryCode: "+90" },
    { name: "Turkmenistan", length: 8, countryCode: "+993" },
    { name: "Tuvalu", length: 7, countryCode: "+688" },
    { name: "Uganda", length: 10, countryCode: "+256" },
    { name: "Ukraine", length: 9, countryCode: "+380" },
    { name: "United Arab Emirates", length: 9, countryCode: "+971" },
    { name: "United Kingdom", length: 10, countryCode: "+44" },
    { name: "United States", length: 10, countryCode: "+1" },
    { name: "Uruguay", length: 8, countryCode: "+598" },
    { name: "Uzbekistan", length: 9, countryCode: "+998" },
    { name: "Vanuatu", length: 7, countryCode: "+678" },
    { name: "Vatican City", length: 10, countryCode: "+379" },
    { name: "Venezuela", length: 10, countryCode: "+58" },
    { name: "Vietnam", length: 10, countryCode: "+84" },
    { name: "Yemen", length: 9, countryCode: "+967" },
    { name: "Zambia", length: 10, countryCode: "+260" },
    { name: "Zimbabwe", length: 9, countryCode: "+263" },
  ]);
  const [phoneError, setPhoneError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [accountNumbers, setAccountNumbers] = useState([]);

  const handleCheckboxChange = (selectedOptions) => {
    setSelectedAssignees(selectedOptions || []);
  };
  const validatePhoneNumber = (phone) => {
    let numericPhone = phone.replace(/[^0-9]/g, "");
    const defaultCountry = { name: "Oman", length: 8, countryCode: "968" };
    let country = countryDetail.find((c) =>
      numericPhone.startsWith(c.countryCode.replace(/[^0-9]/g, ""))
    );
    if (!country) {
      country = defaultCountry;
      numericPhone = defaultCountry.countryCode + numericPhone;
    }
    if (country.countryCode === "+1") {
      if (
        countryDetail.name === "United States" &&
        numericPhone.length !== 11
      ) {
        setPhoneError(
          "United States phone numbers should be exactly 10 digits long."
        );
        return false;
      } else if (
        countryDetail.name === "Canada" &&
        numericPhone.length !== 12
      ) {
        setPhoneError("Canada phone numbers should be exactly 11 digits long.");
        return false;
      }
    } else {
      const phoneNumberLength =
        numericPhone.length - country.countryCode.replace(/[^0-9]/g, "").length;
      if (phoneNumberLength !== country.length) {
        setPhoneError(
          `${country.name} phone numbers should be exactly ${country.length} digits long.`
        );
        return false;
      }
    }
    setPhoneError("");
    return true;
  };
  useEffect(() => {
    const fetchFieldAgentsByWorkFlowData = async () => {
      try {
        const agents = await fetchAgentByWorkflow(activityId); // Fetch field agents API call
        setFieldAgents(agents); // Set the fetched agents
      } catch (error) {
        console.error("Failed to fetch field agents:", error);
      }
    };
    if (activityId) {
      fetchFieldAgentsByWorkFlowData();
    }
  }, [activityId])

  const handleFieldAgentChange = (event) => {
    const selectedAgentId = event.target.value;
    setSelectedAssigneeId(selectedAgentId); // Update the assigneeId state
  };
  const handleInputChange = (event, setter) => {
    setter(event.target.value);
  };

  const removeAccountNumber = (index) => {
    setAccountNumbers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAccountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setAccountNo(value);
    }
  };

  const addAccountNumberChip = (rawValue) => {
    const nextValue = String(rawValue ?? "").trim();
    if (!nextValue) return false;
    setAccountNumbers((prev) => {
      if (prev.includes(nextValue)) return prev;
      return [...prev, nextValue];
    });
    setAccountNo("");
    setAreaCode("");
    setLatitude("");
    setLongitude("");
    return true;
  };

  const handleAccountKeyPress = (e) => {
    if (e.key === "Enter" && accountNo?.trim()) {
      e.preventDefault();
      addAccountNumberChip(accountNo);
      return;
    }
    if (e.key === "Tab" && accountNo?.trim()) {
      addAccountNumberChip(accountNo);
    }
  };

  const searchAccountNO = (e) => {
    if (accountNo?.trim()) {
      e.preventDefault();
      addAccountNumberChip(accountNo);
    }
  }

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 8);
    const match = cleaned.match(/^(\d{0,4})(\d{0,4})$/);
    if (!match) return "";
    const [, part1, part2] = match;
    return `${part1}${part2 ? " " + part2 : ""}`;
  };

  const formatPhoneNumberForBackend = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 8); // Remove non-digits and limit to 8 digits
    if (!cleaned) return ""; // handle empty
    return `968${cleaned}`;
  };

  const handleSubmit = async () => {
    if (!remarks) {
      setRemarksError(translations[langMode].remarksError);
      return;
    }

    setRemarksError(""); // Reset error if Remarks is filled

    const attachmentError = validateAttachments(proofFiles, langMode);
    if (attachmentError) {
      toast.error(attachmentError);
      return;
    }
    setIsLoading(true);

    // The files travel with the request itself as multipart `attachments`
    // parts (see CreateRc / CreateMultipleRcWithOrg), so no S3 pre-upload.
    const formattedPhone = formatPhoneNumberForBackend(mobileNo);
    const data = {
      accountNo: accountNumbers?.[0],
      areaCode,
      area: "Uptown", // Optional field
      mobileNumber: formattedPhone,
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
      assigneeIds: selectedAssignees?.map((item) => item?.value),
      remarks,
      forInteractiveRc: isInteractiveRC,
      organizationId: JSON.parse(localStorage.getItem("selectedOrgId")),
      fromDate,
      toDate,
      projectId: (localStorage.getItem("CustprojectId") || "").replace(/"/g, ""),
    };

    const create_Multi_rc_data = {
      accountNo: accountNumbers,
      assigneeIds: selectedAssignees?.map((item) => item?.value),
      remarks,
      isForInteractiveRc: isInteractiveRC,
      fromDate,
      toDate,
      projectId: (localStorage.getItem("CustprojectId") || "").replace(/"/g, ""),
    };

    try {
      const result =
        accountNumbers.length > 1
          ? await CreateMultipleRcWithOrg(create_Multi_rc_data, proofFiles)
          : await CreateRc(data, proofFiles);

      setIsLoading(false);
      console.log("API Response: ", result);
      if (result?.header?.code === 600) {
        setIsLoading(false);
        toast.success(result?.body?.value);
        setAccountNo("");
        setAreaCode("");
        setFromDate("");
        setToDate("");
        setLatitude("");
        setMobileNo("");
        setLongitude("");
        setRemarks("");
        setSelectedAssigneeId("");
        setSelectedAssignees([]);
        setAccountNumbers([]);
        setSelectedAssignees([])
        setProofFiles([]);
      }
      else if (result?.header?.code === 601) {
        setIsLoading(false);
        toast.error(result?.body?.value);
      }
      else {
        setIsLoading(false);
        toast.error(result?.body?.value || result?.body?.error);
        // toast.error("No response received from the server.");
        // console.error("No response data from API.");
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error submitting data to the API:", error);
      toast.error(translations[langMode].unexpectederroroccured);
    }
  };

  useEffect(() => {
    if (accountNumbers) {
      console.log("Selected Org Id: ", JSON.parse(localStorage.getItem("selectedOrgId")));
      const orgid = JSON.parse(localStorage.getItem("selectedOrgId"))
      fetchFieldsValues(orgid, accountNumbers, ((localStorage.getItem("CustprojectId") || "").replace(/"/g, "")))
        .then((data) => {
          if (data?.body?.value) {
            setAreaCode(data.body.value["Area Code"] || "");
            setLatitude(data.body.value.Latitude || "");
            setLongitude(data.body.value.Longitude || "");
          }
        })
        .catch((error) => {
          console.error("Error fetching account details:", error);
        });
    }
  }, [accountNumbers, JSON.parse(localStorage.getItem("selectedOrgId"))]);

  useEffect(() => {
    if (lastItemRef.current) {
      lastItemRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [accountNumbers]);

  const getTomorrowDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1); // Add 1 day
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const minDate = getTomorrowDate();
  const minDateObj = useMemo(() => parseYmd(minDate), [minDate]);

  const handlePasteExcel = (e) => {
    e.preventDefault();
    let values = e.clipboardData
      .getData("text")
      .split(/\r?\n|\t/)
      .map((line) => line.trim())
      .filter((line) => line !== "");
    setAccountNumbers((prev) => {
      const unique = new Set(prev);
      values.forEach((value) => unique.add(value));
      return Array.from(unique);
    });
  };

   const isDisabledWithPermission = isLoading || !customVarPerm;

  useEffect(() => {
    if (!onHeaderActionsChange) return undefined;

    onHeaderActionsChange(
      <CommonButton colorIntent="success"
        onClick={handleSubmit}
        disabled={isDisabledWithPermission}
        className={`submitrc w-full shrink-0 !rounded-lg !px-4 !py-2.5 !gap-2 sm:w-auto ${isDisabledWithPermission ? "cursor-not-allowed opacity-50" : ""}`}
        variant="primary"
        tooltipText={translations[langMode].submitspecialRc}
        tooltipPosition="top"
      >
        {isLoading ? (
          <FaSpinner className="animate-spin h-5 w-5 mr-2" />
        ) : (
          translations[langMode].submitRc
        )}
      </CommonButton>
    );

    return () => onHeaderActionsChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountNumbers,
    areaCode,
    mobileNo,
    latitude,
    longitude,
    remarks,
    selectedAssignees,
    fromDate,
    toDate,
    proofFiles,
    isInteractiveRC,
    isLoading,
    isDisabledWithPermission,
    langMode,
  ]);

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-3 md:gap-0">
      <div className="mb-5 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <div className="flex flex-col">
          <label className="text-sm font-semibold mb-1 ml-2">{translations[langMode].todate}</label>
          <DatePicker
            selected={parseYmd(toDate)}
            onChange={(date) => setToDate(date ? formatYmd(date) : "")}
            placeholderText="YYYY-MM-DD"
            minDate={minDateObj}
            className={FIELD_INPUT_CLASS}
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement="bottom-start"
            popperContainer={({ children }) => (
              <div style={{ position: "fixed", zIndex: 100000 }}>{children}</div>
            )}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold mb-1 ml-2">{translations[langMode].accno}</label>
          <div className="relative">
            <div className="px-2 py-1 rounded-lg border border-gray-300 bg-white min-h-[50px] flex items-center gap-2 focus-within:border-blue-500 focus-within:outline-none">
              <div
                className="flex-1 min-w-0 max-h-[120px] overflow-y-auto flex flex-wrap items-center gap-1"
              >
                {accountNumbers?.map((number, index) => (
                  <div
                    key={index}
                    ref={
                      index === accountNumbers.length - 1 ? lastItemRef : null
                    }
                    className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md flex items-center gap-1 text-sm border border-gray-200"
                  >
                    <span>{number}</span>
                    <button
                      onClick={() => removeAccountNumber(index)}
                      aria-label={translations[langMode]?.removeAccountNumber ?? "Remove account number"}
                      className="text-gray-400 hover:text-gray-600 ml-0.5 inline-flex items-center justify-center"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
                <input
                  type="text"
                  value={accountNo}
                  onChange={handleAccountChange}
                  onKeyDown={handleAccountKeyPress}
                  onPaste={handlePasteExcel}
                  placeholder={
                    accountNumbers?.length === 0
                      ? translations[langMode].typeEnter
                      : ""
                  }
                  className="flex-1 min-w-[120px] h-8 outline-none border-none bg-transparent focus:ring-0 focus:border-transparent rounded-lg text-gray-700 text-sm"
                />
              </div>
              <button
                onClick={searchAccountNO}
                className="lg:hidden inline-flex items-center justify-center h-9 w-9 bg-[#236DB4] text-white rounded-lg shrink-0"
              >
                <Search size={18} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-2">
              {translations[langMode].pressEnter}
            </p>
          </div>
        </div>
        {[
          { label: translations[langMode].areacode, value: areaCode, setter: setAreaCode },
          { label: translations[langMode].Latitude, value: latitude, setter: setLatitude },
          { label: translations[langMode].Longitude, value: longitude, setter: setLongitude },
        ].map((field, index) => (
          <div key={index} className="flex flex-col">
            <label className="text-sm font-semibold mb-1 ml-2">
              {field.label}
            </label>
            <input
              type="text"
              value={field.value}
              onChange={(e) => handleInputChange(e, field.setter)}
              placeholder={field.label}
              className={FIELD_INPUT_CLASS}
            />
          </div>
        ))}
        <div className="flex flex-col">
          <label className="text-sm font-semibold mb-1 ml-2">{translations[langMode].remark}</label>
          <input
            type="text"
            value={remarks}
            onChange={(e) => handleInputChange(e, setRemarks)}
            placeholder={translations[langMode].remark}
            className={FIELD_INPUT_CLASS}
          />
          {remarksError && (
            <p className="text-red-500 text-xs">{remarksError}</p>
          )}
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold mb-1 ml-2">{translations[langMode].Phone}</label>
          <div className="flex items-center rounded-[6px] w-full bg-white min-h-[50px] py-[5px] px-[10px] border border-[#D1D5DB] text-sm focus-within:border-blue-500">
            <div className="flex items-center gap-2">
              <span>OM</span>
              <span className="font-bold">+968</span>
            </div>
            <input
              className={`block p-2 w-full text-sm text-gray-900 bg-transparent rounded-md
                            appearance-none border-0 focus:ring-0 focus:!shadow-none peer ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                }`}
              type="tel"
              name="phone"
              value={mobileNo}
              onChange={(e) => {
                const raw = e.target.value;
                const cleaned = raw.replace(/\D/g, "");
                const formatted = formatPhoneNumber(cleaned);
                setMobileNo(formatted);
              }}
              placeholder="9123 4567"
              maxLength={9}
            />
          </div>
          {phoneError && (
            <div className="text-xs" style={{ color: "red", marginTop: "2px" }}>
              {phoneError}
            </div>
          )}
        </div>
        {/* Sakshi - 2026-08-03: menuPosition="fixed" keeps the portaled menu out of
            document flow - with the default absolute positioning it grew the page
            height and broke the layout below the field agent picker. */}
        <div className="relative flex flex-col">
          <label className="text-sm font-semibold mb-1 ml-2">
            {translations[langMode].fieldAgent}
          </label>
          <ReactSelector
            options={Array.isArray(fieldAgents) ? (fieldAgents || [])?.map((item) => ({ value: item?.userId, label: item?.name })) : []}
            isSearchable={true}
            value={fieldAgents ? selectedAssignees : []}
            menuPlacement="auto"
            menuPosition="fixed"
            menuPortalTarget={document.body}
            components={{ Placeholder }}
            placeholder={translations[langMode]?.selectFieldAgent ?? "Select Field Agent"}
            onChange={handleCheckboxChange}
            isMulti
            selectedValuesMaxHeight="64px"
            isClearable={false}
            overflow="overlay"
            classNamePrefix="select"
            classNames={{
              control: () => "!h-[50px] !min-h-[50px] !rounded-[10px] !border !border-gray-300 !bg-white",
              menu: () => "!rounded-[10px] !overflow-hidden",
              valueContainer: () => "!px-3",
            }}
          />
        </div>
        <MultiAttachInput
          files={proofFiles}
          onChange={setProofFiles}
          theme={theme}
          langMode={langMode}
        />
      </div>
    </div>
  );
};

export default SpecialRC;


